import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, tap, throwError } from 'rxjs';
import { Pagination } from '../../interfaces/deliveries.interface';
import { IMotivoNoEntrega } from '../../interfaces/motivo-no-entrega.interface';
import { MotivoNoEntregaService } from '../../services/configuracion/motivo-no-entrega.service';
import { MotivoNoEntregaActions } from './motivo-no-entrega.action';

export class MotivoNoEntregaStateModel {
  motivos: Pagination<IMotivoNoEntrega> = { items: [], page: 1, pageSize: 10, total: 0 };
  loading: boolean = false;
  error: any | null = null;
}

@State<MotivoNoEntregaStateModel>({
  name: 'motivoNoEntrega',
  defaults: {
    motivos: { items: [], page: 1, pageSize: 10, total: 0 },
    loading: false,
    error: null,
  },
})
@Injectable()
export class MotivoNoEntregaState {
  constructor(private motivoService: MotivoNoEntregaService) {}

  @Selector()
  static getMotivos(state: MotivoNoEntregaStateModel): Pagination<IMotivoNoEntrega> {
    return state.motivos;
  }

  @Selector()
  static getLoading(state: MotivoNoEntregaStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static getError(state: MotivoNoEntregaStateModel): any | null {
    return state.error;
  }

  @Action(MotivoNoEntregaActions.GetMotivos)
  getMotivos(
    ctx: StateContext<MotivoNoEntregaStateModel>,
    action: MotivoNoEntregaActions.GetMotivos,
  ) {
    ctx.patchState({ loading: true, error: null });
    const page = action.page ?? 1;
    const pageSize = action.pageSize ?? 10;
    return this.motivoService.getMotivos(page, pageSize).pipe(
      tap((response) => {
        ctx.patchState({ motivos: response.data, loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.error?.message });
        return throwError(() => error);
      }),
    );
  }

  @Action(MotivoNoEntregaActions.CreateMotivo)
  createMotivo(
    ctx: StateContext<MotivoNoEntregaStateModel>,
    action: MotivoNoEntregaActions.CreateMotivo,
  ) {
    ctx.patchState({ loading: true, error: null });
    return this.motivoService.createMotivo(action.dto).pipe(
      tap(() => {
        const { page, pageSize } = ctx.getState().motivos;
        ctx.dispatch(new MotivoNoEntregaActions.GetMotivos(page, pageSize));
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.error?.message });
        return throwError(() => error);
      }),
    );
  }

  @Action(MotivoNoEntregaActions.UpdateMotivo)
  updateMotivo(
    ctx: StateContext<MotivoNoEntregaStateModel>,
    action: MotivoNoEntregaActions.UpdateMotivo,
  ) {
    ctx.patchState({ loading: true, error: null });
    return this.motivoService.updateMotivo(action.id, action.dto).pipe(
      tap(() => {
        const { page, pageSize } = ctx.getState().motivos;
        ctx.dispatch(new MotivoNoEntregaActions.GetMotivos(page, pageSize));
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.error?.message });
        return throwError(() => error);
      }),
    );
  }

  @Action(MotivoNoEntregaActions.DeleteMotivo)
  deleteMotivo(
    ctx: StateContext<MotivoNoEntregaStateModel>,
    action: MotivoNoEntregaActions.DeleteMotivo,
  ) {
    ctx.patchState({ loading: true, error: null });
    return this.motivoService.deleteMotivo(action.id).pipe(
      tap(() => {
        const { page, pageSize } = ctx.getState().motivos;
        ctx.dispatch(new MotivoNoEntregaActions.GetMotivos(page, pageSize));
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.error?.message });
        return throwError(() => error);
      }),
    );
  }
}
